import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icons";
import { useScrollLock } from "./Sheet";

const MAX = 4;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Full-screen photos. Desktop: click to zoom where you clicked, move the
// mouse to look around. Phone: pinch or double-tap to zoom, drag to pan,
// swipe to the next photo when not zoomed. Esc / arrows on a keyboard.
export default function Lightbox({ images, index, onIndex, onClose }) {
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const stage = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);
  const fine = useRef(typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches);
  useScrollLock(true);

  const reset = useCallback(() => setView({ s: 1, x: 0, y: 0 }), []);
  const go = useCallback(
    (d) => {
      reset();
      onIndex((index + d + images.length) % images.length);
    },
    [index, images.length, onIndex, reset]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  // Keep the zoomed photo inside the screen.
  const bound = (s, x, y) => {
    const r = stage.current?.getBoundingClientRect();
    if (!r) return { s, x, y };
    const mx = ((s - 1) * r.width) / 2;
    const my = ((s - 1) * r.height) / 2;
    return { s, x: clamp(x, -mx, mx), y: clamp(y, -my, my) };
  };

  // Zoom to scale s keeping the point (cx, cy) (client coords) still.
  const zoomAt = (s, cx, cy, from = view) => {
    const r = stage.current.getBoundingClientRect();
    const px = cx - (r.left + r.width / 2);
    const py = cy - (r.top + r.height / 2);
    const k = s / from.s;
    return bound(s, px - (px - from.x) * k, py - (py - from.y) * k);
  };

  const onPointerDown = (e) => {
    stage.current.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      const [a, b] = pts;
      gesture.current = { type: "pinch", dist: Math.hypot(a.x - b.x, a.y - b.y), start: view, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    } else {
      gesture.current = { type: "drag", x: e.clientX, y: e.clientY, start: view, moved: false };
    }
  };

  const onPointerMove = (e) => {
    if (fine.current && e.pointerType === "mouse" && view.s > 1 && !pointers.current.size) {
      // Mouse: the zoomed photo follows the cursor, no dragging needed.
      const r = stage.current.getBoundingClientRect();
      const fx = (e.clientX - r.left) / r.width - 0.5;
      const fy = (e.clientY - r.top) / r.height - 0.5;
      setView((v) => bound(v.s, -fx * (v.s - 1) * r.width, -fy * (v.s - 1) * r.height));
      return;
    }
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.type === "pinch" && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const s = clamp((g.start.s * Math.hypot(a.x - b.x, a.y - b.y)) / g.dist, 1, MAX);
      setView(zoomAt(s, g.cx, g.cy, g.start));
    } else if (g.type === "drag") {
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (Math.abs(dx) + Math.abs(dy) > 6) g.moved = true;
      if (g.start.s > 1) setView(bound(g.start.s, g.start.x + dx, g.start.y + dy));
      else setView({ s: 1, x: dx, y: 0 });
    }
  };

  const onPointerUp = (e) => {
    const g = gesture.current;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size) return;
    gesture.current = null;
    if (!g) return;
    if (g.type === "pinch") {
      if (view.s < 1.05) reset();
      return;
    }
    if (g.start.s === 1 && g.moved) {
      const dx = e.clientX - g.x;
      if (Math.abs(dx) > 60 && images.length > 1) go(dx < 0 ? 1 : -1);
      else reset();
      return;
    }
    if (g.moved) return;
    // A tap / click: mouse zooms on one click, touch on a double tap.
    const now = Date.now();
    const isTouch = e.pointerType !== "mouse";
    if (isTouch && now - lastTap.current > 300) {
      lastTap.current = now;
      return;
    }
    lastTap.current = 0;
    setView(view.s > 1 ? { s: 1, x: 0, y: 0 } : zoomAt(isTouch ? 2.5 : 2.2, e.clientX, e.clientY));
  };

  const onWheel = (e) => {
    if (!fine.current) return;
    const s = clamp(view.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15), 1, MAX);
    setView(s === 1 ? { s: 1, x: 0, y: 0 } : zoomAt(s, e.clientX, e.clientY));
  };

  const img = images[index];
  return createPortal(
    <div className="sh-lb" role="dialog" aria-modal="true" aria-label="Photos">
      <div className="sh-lb-bar">
        <span className="sh-lb-count">
          {index + 1} / {images.length}
        </span>
        <span className="sh-lb-hint">{view.s > 1 ? "" : fine.current ? "Click to zoom" : "Double-tap or pinch to zoom"}</span>
        <button className="sh-iconbtn" onClick={onClose} aria-label="Close photos" autoFocus>
          <Icon name="close" size={24} />
        </button>
      </div>
      <div
        className={`sh-lb-stage${view.s > 1 ? " zoomed" : ""}`}
        ref={stage}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        <img
          key={img.url}
          src={img.url}
          alt={img.alt || ""}
          draggable="false"
          style={{ transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.s})`, transition: gesture.current ? "none" : undefined }}
        />
      </div>
      {images.length > 1 && (
        <>
          <button className="sh-lb-nav prev" onClick={() => go(-1)} aria-label="Previous photo">
            <Icon name="left" size={28} />
          </button>
          <button className="sh-lb-nav next" onClick={() => go(1)} aria-label="Next photo">
            <Icon name="right" size={28} />
          </button>
          <div className="sh-lb-thumbs">
            {images.map((im, i) => (
              <button key={im.url} className={i === index ? "on" : ""} onClick={() => {
                  reset();
                  onIndex(i);
                }} aria-label={`Photo ${i + 1}`}>
                <img src={im.url} alt="" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}
